import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import moment from 'moment';
import { Card, ButtonGroup, Button } from 'react-bootstrap';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Title, Tooltip, Legend);

const getCssVar = (v) => getComputedStyle(document.documentElement).getPropertyValue(v).trim();

const timeRanges = [
  { label: '1D',  period: '1D',  timeframe: '5Min', maxTicks: 8,  fmt: 'h:mm a' },
  { label: '1W',  period: '1W',  timeframe: '1H',   maxTicks: 7,  fmt: 'ddd M/D' },
  { label: '1M',  period: '1M',  timeframe: '1D',   maxTicks: 8,  fmt: 'M/D' },
  { label: '6M',  period: '6M',  timeframe: '1D',   maxTicks: 6,  fmt: 'MMM D' },
  { label: 'YTD', period: 'YTD', timeframe: '1D',   maxTicks: 6,  fmt: 'MMM D' },
  { label: '1Y',  period: '1Y',  timeframe: '1D',   maxTicks: 6,  fmt: 'MMM YYYY' },
  { label: 'ALL', period: 'ALL', timeframe: '1D',   maxTicks: 6,  fmt: 'MMM YYYY' },
];

const PortfolioChart = () => {
  const [chartData, setChartData]       = useState(null);
  const [selectedRange, setSelectedRange] = useState(timeRanges[0]);
  const [isLoading, setIsLoading]       = useState(true);
  const [colors, setColors]             = useState(readColors());

  function readColors() {
    return {
      primary:   getCssVar('--rv-primary'),
      bodyBg:    getCssVar('--rv-surface-raised'),
      bodyColor: getCssVar('--rv-ink'),
      border:    getCssVar('--rv-line'),
    };
  }

  // Re-read CSS vars on ANY attribute change on <html> (data-theme or data-bs-theme)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(readColors()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-bs-theme'],
    });
    return () => observer.disconnect();
  }, []);

  // When colors update, re-apply them to the existing chart dataset
  useEffect(() => {
    if (!chartData) return;
    setChartData((prev) => ({
      ...prev,
      datasets: prev.datasets.map((ds) => ({
        ...ds,
        borderColor: colors.primary,
        backgroundColor: colors.primary + '18',
        pointHoverBackgroundColor: colors.bodyBg,
        pointHoverBorderColor: colors.primary,
      })),
    }));
  }, [colors]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPortfolioHistory = async ({ period, timeframe, fmt }) => {
    setIsLoading(true);
    try {
      const instance = axios.create({
        baseURL: 'https://18z7zyxvn4.execute-api.us-east-2.amazonaws.com',
        headers: {
          Authorization: 'Bearer ' + sessionStorage.getItem('jwt'),
          'Content-Type': 'application/json',
        },
      });

      const { data } = await instance.get('/api/trading', {
        params: { period, timeframe, extended_hours: true },
      });

      setChartData({
        labels: data.timestamp.map((ts) => moment(ts * 1000).format(fmt)),
        datasets: [
          {
            label: 'Equity',
            data: data.equity,
            borderColor: colors.primary,
            backgroundColor: colors.primary + '18',
            fill: true,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: colors.bodyBg,
            pointHoverBorderColor: colors.primary,
            pointHoverBorderWidth: 2,
            tension: 0.3,
          },
        ],
      });
    } catch (err) {
      console.error('Error fetching portfolio history:', err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPortfolioHistory(selectedRange);
  }, [selectedRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title:  { display: false },
      tooltip: {
        backgroundColor: colors.bodyBg,
        titleColor: colors.bodyColor,
        bodyColor: colors.bodyColor,
        borderColor: colors.border,
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (ctx) =>
            ' $' + ctx.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        grid:  { color: colors.bodyColor + '18' },
        ticks: {
          color: colors.bodyColor,
          callback: (v) => '$' + v.toLocaleString(),
        },
      },
      x: {
        grid:  { display: false },
        ticks: {
          color: colors.bodyColor,
          maxTicksLimit: selectedRange.maxTicks,
          maxRotation: 0,
        },
      },
    },
    interaction: { intersect: false, mode: 'index' },
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Card.Title className="mb-0">Portfolio Value</Card.Title>
          <ButtonGroup size="sm">
            {timeRanges.map((range) => (
              <Button
                key={range.label}
                variant={selectedRange === range ? 'primary' : 'outline-primary'}
                onClick={() => setSelectedRange(range)}
              >
                {range.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: 360 }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : (
          <div style={{ height: 360, position: 'relative' }}>
            {chartData && <Line data={chartData} options={options} />}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PortfolioChart;
